<template>
  <div :class="['job-info', `job-info--${variant}`]">
    <div class="job-info-main">
      <div v-hover-tip.over="titleText" class="job-info-title">{{ titleText }}</div>
      <div class="job-info-details">
        <span v-hover-tip.over="job.affiliationName" class="job-info-details-item">{{ job.affiliationName || '-' }}</span>
        <span class="job-info-separator">|</span>
        <span v-hover-tip.over="positionText" class="job-info-details-item">{{ positionText || '-' }}</span>
      </div>
    </div>
    <!-- reserveTag 时无论有无标签都占住标签位，列表里各行左侧内容宽度一致 -->
    <div v-if="tag || reserveTag" :class="['job-info-tag-slot', { 'is-reserved': reserveTag }]">
      <span v-if="tag" :class="['job-info-tag', `job-info-tag--${tag.type || 'info'}`]">{{ tag.text }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { JobInfoData, JobInfoTag } from './types'

// 求人信息：「ID | 标题」+「所属 | 职种」两行，右侧可带状态标签
// variant=table 用于表格单元格，variant=option 用于下拉选项（颜色继承 el-option 的选中/hover 色）
const props = withDefaults(defineProps<{
  job: JobInfoData
  tag?: JobInfoTag
  variant?: 'table' | 'option'
  /** 固定预留标签位（列表中部分行有标签时使用，保证各行内容宽度一致） */
  reserveTag?: boolean
}>(), {
  tag: undefined,
  variant: 'table',
  reserveTag: false,
})

const titleText = computed(() => `${props.job.jobId} | ${props.job.title}`)
const positionText = computed(() => props.job.positionName || props.job.positionTypeName)
</script>

<style lang="scss" scoped>
.job-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  min-width: 0;
  line-height: normal;
}

.job-info-main {
  flex: 1;
  min-width: 0;
}

.job-info-title {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.job-info-details {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  font-size: 12px;
}

.job-info-details-item {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.job-info-separator {
  margin: 0 4px;
  color: #C9D3D9;
}

.job-info--table {
  padding: 8px 0;

  .job-info-title {
    font-weight: 700;
    color: #1d2129;
  }

  .job-info-details {
    margin-top: 8px;
    font-size: 13px;
    color: #1d2129;
  }
}

.job-info--option {
  .job-info-details {
    color: #86909C;
    // 选中项会继承 el-option 的加粗，第二行保持常规字重
    font-weight: normal;
  }
}

.job-info-tag-slot {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;

  // 宽度按最长的标签（如「暂不考虑」）预留
  &.is-reserved {
    width: 72px;
  }
}

.job-info-tag {
  flex-shrink: 0;
  white-space: nowrap;
  padding: 2px 8px;
  border-radius: 463px;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;

  &--success {
    background-color: #DBF1F1;
    color: #009E99;
  }

  &--primary {
    background-color: #EDEFFD;
    color: #1F69FF;
  }

  &--info {
    background-color: #F2F3F5;
    color: #86909C;
  }
}
</style>

<!-- 非 scoped：需要匹配外层 el-option 的选中 / hover 状态 -->
<style lang="scss">
// 下拉宽度与输入框一致（el-select 需设 fit-input-width），选项内容过长时 JobInfo 内部省略，
// 这里保证 el-option 本身不被内容撑宽
.el-select-dropdown__item:has(> .job-info) {
  overflow: hidden;
}

// 选中 / hover 时第二行也跟随主题色（与原 job-option 的全局样式一致）
.el-select-dropdown__item.is-selected,
.el-select-dropdown__item.is-hovering {
  .job-info--option .job-info-details {
    color: var(--el-color-primary);
  }
}

// schedule-interview-dialog 的非 scoped 样式会给所有选中项里的 span 加 padding-right: 10px !important，
// 导致选中/未选中时间距不一致，这里用更高优先级还原
.el-select-dropdown__list .el-select-dropdown__item.is-selected .job-info {
  .job-info-details-item,
  .job-info-separator {
    padding-right: 0 !important;
  }

  .job-info-tag {
    padding-right: 8px !important;
  }
}
</style>
